import type { ScanPhoto, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import type { ReactNode } from 'react';

import type { ScopeNavigatorContext } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import {
  getContractorRows,
  getFinancialRows,
  getLaborBasisLabel,
  getMaterialRows,
  getStatusPresentation,
} from '@/Client/Partials/CloseoutReviewFlow/closeoutModelBuilders';
import type { PhotoDetailModel } from '@/Client/Partials/PhotoDetail';
import {
  formatPhotoDate,
  getPhotoContributorLabel,
  getPhotoDate,
  getPhotoFieldUserName,
  getPhotoSourceLabel,
  isVideoFrame,
} from '@/Client/Partials/PhotoDetail/photoDetailLib';

import type { GalleryPhotoOccurrence } from '../galleryPhotoOperations';

/**
 * The takeover's own adapter onto the shared photo detail overlay, built from the occurrence it
 * already tracks.
 *
 * The cost rows come from `closeoutModelBuilders`: they read the task's own
 * `optimized.reducedValues`, which the takeover has in hand through `tasksById`, so there is
 * nothing to subscribe to. `walkTimeline` stays empty; the manager has no cell addresses. The
 * overlay renders a row with no value when a fact is absent.
 */
export const buildPhotoManagerDetailModel = (
  {
    occurrence,
    occurrences,
    task,
    sectionPhotos,
    groupPhotos,
    groupTitle,
    userNameById,
    contractorContext,
    notes,
    walkDateControl,
    selectedKey,
  }: {
    occurrence: GalleryPhotoOccurrence;
    /** Every photo the takeover is showing, in section order — the overlay's filmstrip. */
    occurrences: readonly GalleryPhotoOccurrence[];
    /** The task the photo hangs off, when it hangs off one. Source of every cost row. */
    task?: ScanTaskOptimized;
    /** The photo's own section, for the overlay's first rail — mockup `.po-strip-title`. */
    sectionPhotos?: readonly GalleryPhotoOccurrence[];
    /** The group's own photos, for the overlay's group rail. */
    groupPhotos?: readonly GalleryPhotoOccurrence[];
    userNameById?: Record<string, string | undefined>;
    /** Resolves the task's contractor, honouring the scan's defaults-by-trade. */
    contractorContext?: ScopeNavigatorContext;
    notes?: string;
    /** The owning group's title, for a task photo's subtitle. Ids are not for reading. */
    groupTitle?: string;
    /** The host's control on the walk date, when it offers one. */
    walkDateControl?: ReactNode;
    /**
     * Which photo the strip is stepped to, when that has moved off the opened one. Only the `selected`
     * flags follow it — the rest of the pane stays with `occurrence`, the photo the takeover opened —
     * so the overlay can rebuild the model as the reader steps without the modal snapping the strip
     * back to where it started. Defaults to the opened photo.
     */
    selectedKey?: string;
  },
): PhotoDetailModel => ({
  id: occurrence.key,
  taskId: occurrence.context.taskId,
  title: occurrence.sectionTitle,
  subtitle: occurrence.context.taskId ? groupTitle : undefined,
  heroPhotoId: occurrence.photo.photoId,
  notes,
  description: task?.template?.description?.trim() || undefined,
  /* Mockup's Photo info: what this photo is, who made it and how. "Frame chosen by Kai" appears
     only for a still Kai pulled from a video — there is no frame to have chosen otherwise. */
  photoInfo: [
    ...(task ? [{ label: 'Status', value: getStatusPresentation(task).label ?? '—' }] : []),
    { label: 'Walk date', value: formatPhotoDate(getPhotoDate(occurrence.photo)) ?? '—', control: walkDateControl },
    {
      label: getPhotoContributorLabel(occurrence.photo),
      value: getPhotoFieldUserName(occurrence.photo, userNameById) ?? '—',
    },
    { label: 'Source', value: getPhotoSourceLabel(occurrence.photo) },
    ...(isVideoFrame(occurrence.photo) ? [{ label: 'Frame chosen by', value: 'Kai', tone: 'accent' as const }] : []),
  ],
  taskInfo: task
    ? [
      { label: 'Task ID', value: task.id },
    ]
    : [],
  contractor: task ? getContractorRows(task, contractorContext) : [],
  laborBasis: getLaborBasisLabel(task),
  financials: task ? getFinancialRows(task) : [],
  materials: task ? getMaterialRows(task) : [],
  groupPhotos: (groupPhotos ?? []).map(candidate => ({
    id: candidate.key,
    title: candidate.sectionTitle,
    photoId: candidate.photo.photoId,
    captionLabel: getPhotoCaption(candidate.photo),
  })),
  /* The rail is the photo's own section — the mockup titles it with the task and counts only its
     photos — while the arrows cross every photo the takeover is showing. */
  thumbnails: (sectionPhotos ?? occurrences).map(candidate => ({
    id: candidate.key,
    title: candidate.sectionTitle,
    photoId: candidate.photo.photoId,
    selected: candidate.key === (selectedKey ?? occurrence.key),
    captionLabel: getPhotoCaption(candidate.photo),
  })),
  navThumbnails: occurrences.map((candidate, index) => ({
    id: candidate.key,
    title: candidate.sectionTitle,
    photoId: candidate.photo.photoId,
    selected: candidate.key === (selectedKey ?? occurrence.key),
    captionLabel: getPhotoCaption(candidate.photo),
    metaLabel: getCaptionMeta(candidate, index, occurrences.length, userNameById),
  })),
});

/** Mockup `capMeta`: who took it, when, where it sits in the set, and how it arrived. */
const getCaptionMeta = (
  occurrence: GalleryPhotoOccurrence,
  index: number,
  total: number,
  userNameById?: Record<string, string | undefined>,
): string =>
  [
    getPhotoFieldUserName(occurrence.photo, userNameById),
    formatPhotoDate(getPhotoDate(occurrence.photo)),
    `${index + 1} of ${total}`,
    getPhotoSourceLabel(occurrence.photo),
  ].filter(Boolean).join(' · ');

const getPhotoCaption = (photo: ScanPhoto): string | undefined => formatPhotoDate(getPhotoDate(photo));
